import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutGrid, ChevronDown } from 'lucide-react';
import { buildProductsPath } from '../lib/seoUrls';
import { useDynamicTaxonomyTranslation } from '../hooks/useDynamicTaxonomyTranslation';
import { useCategoriesData } from '../hooks/useCategoriesData';

/**
 * Site-wide Categories mega-menu dropdown.
 * Lives in the header so it shows on every page.
 * Fetches DB taxonomy once, deduplicates, and auto-translates names via
 * the existing dynamic taxonomy hook so non-English visitors see translated
 * category & subcategory names.
 */
export const CategoriesMegaMenu = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [open, setOpen] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const ref = useRef(null);
  const { categories } = useCategoriesData();

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setActiveCat(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Ensure all DB names are translated to current language
  useDynamicTaxonomyTranslation(categories);

  const close = () => { setOpen(false); setActiveCat(null); };

  if (categories.length === 0) return null;

  const selected = activeCat || categories[0];

  return (
    <div className="relative" ref={ref} data-testid="header-categories-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 text-[#1C241E] hover:text-[#D96C4A] text-sm font-medium"
        data-testid="categories-menu-trigger"
        aria-expanded={open}
      >
        <LayoutGrid className="w-4 h-4" />
        <span>{t('nav.categories', 'Categories')}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute left-1/2 -translate-x-1/2 mt-3 w-[min(92vw,820px)] bg-white rounded-2xl shadow-2xl border border-[rgba(28,36,30,0.12)] overflow-hidden z-50 grid grid-cols-1 md:grid-cols-[220px_1fr]"
          data-testid="categories-menu-panel"
        >
          {/* Left column — category list */}
          <div className="bg-[#FAFAF8] border-r border-[rgba(28,36,30,0.06)] py-3 max-h-[60vh] overflow-y-auto">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onMouseEnter={() => setActiveCat(cat)}
                onFocus={() => setActiveCat(cat)}
                onClick={() => setActiveCat(cat)}
                className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${selected?.id === cat.id ? 'bg-white text-[#D96C4A] font-medium' : 'text-[#1C241E] hover:bg-white'}`}
                data-testid={`menu-cat-${cat.id}`}
              >
                {cat.image_url && (
                  <img src={cat.image_url} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
                )}
                <span className="truncate flex-1">{t(`cat.${cat.id}`, cat.name)}</span>
                <span className="text-[10px] text-[#5C6B61]">{(cat.subcategories || []).length}</span>
              </button>
            ))}
          </div>

          {/* Right column — subcategories of the hovered category */}
          <div className="p-5 max-h-[60vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-[#1C241E]">
                {t(`cat.${selected.id}`, selected.name)}
              </h4>
              <Link
                to={buildProductsPath(lang, selected.id)}
                onClick={close}
                className="text-xs text-[#D96C4A] hover:underline font-medium"
                data-testid={`menu-viewall-${selected.id}`}
              >
                {t('categories.viewAllInCat', 'View all →')}
              </Link>
            </div>

            {(selected.subcategories || []).length === 0 ? (
              <p className="text-sm text-[#5C6B61]">{t('categories.noSubs', 'No subcategories yet.')}</p>
            ) : (
              <ul className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
                {selected.subcategories.map((sub) => (
                  <li key={sub.id}>
                    <Link
                      to={buildProductsPath(lang, selected.id, sub.id)}
                      onClick={close}
                      className="flex items-center gap-2 text-xs text-[#5C6B61] hover:text-[#D96C4A] py-1"
                      data-testid={`menu-sub-${sub.id}`}
                    >
                      {sub.image_url && (
                        <img src={sub.image_url} alt="" className="w-4 h-4 rounded object-cover flex-shrink-0" />
                      )}
                      <span className="truncate">{t(`sub.${sub.id}`, sub.name)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesMegaMenu;
